import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import TableContainer from "../../../../components/Common/TableContainer";
import CustomMonthPicker from "common/CustomMonthPicker";
import { useDispatch, useSelector } from "react-redux";
import { fetchTimesheetFinalApproval, resetTimesheetFinalApproval, saveTimesheetFinalApproval } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { format, eachDayOfInterval, endOfMonth, startOfMonth } from "date-fns";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";
import useButtonAccess from "components/Common/useButtonAccess";
import { useLocation, useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";

const getDateColumns = (selectedMonth) => {
  if (!selectedMonth) return [];

  const start = startOfMonth(new Date(selectedMonth));
  const end = endOfMonth(new Date(selectedMonth));
  const days = eachDayOfInterval({ start, end });

  return days.map((date) => {
    const formattedKey = format(date, "dd.MM.yy");

    return {
      Header: formattedKey,
      accessor: row => row[formattedKey],  // <-- key fix here!
      id: formattedKey,                    // <-- needed when using accessor function
      Cell: ({ value }) => (
        <div className="text-center">{value || "-"}</div>
      ),
      className: "bg-date",
      applyHeaderClass: true,
    };
  });
};

const TimeSheetFinalApproval = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete, canApprove} = useButtonAccess(accessRight);

  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    finalApprovals,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    finalApprovals: state.TimesheetTransactionApprovalReducer?.finalApprovals,

    loading: state.TimesheetTransactionApprovalReducer?.loading ,
    error: state.TimesheetTransactionApprovalReducer?.error ,
    success: state.TimesheetTransactionApprovalReducer?.success
  }));
  const [selectedRows, setSelectedRows] = useState([]);
  const [timesheetFinalApprovalData, setTimesheetFinalApprovalData] = useState([]);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
  }, [dispatch]);

  useEffect(() => {
    if (finalApprovals && finalApprovals.length > 0) {
      const initializedData = (finalApprovals || []).map(row => ({
        ...row,
        selectedOption: null,
        remarks: "",
        approved_hours:row.hr_approved_hours,
      }));
      setTimesheetFinalApprovalData(initializedData)
    }else{
      setTimesheetFinalApprovalData([])
      dispatch(resetTimesheetFinalApproval())
    }
  }, [finalApprovals])

  useEffect(() => {
      if(success){
          fetchfinalapproval(formik.values)
      }
  },[authDetails,success,dispatch]);

  const fetchfinalapproval = (values)=>{
      const formData = new FormData();
      formData.append("month", values.month);
      formData.append("unit_id", authDetails?.unit_id);
      dispatch(fetchTimesheetFinalApproval(formData));
  }

  const formik = useFormik({
    initialValues: {
      month: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    },
    validationSchema: Yup.object({
      month: Yup.string().nullable().required("Month is required"),
    }),
    onSubmit: (values) => {
      fetchfinalapproval(values)
    },
  });

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleHourChange = (value, rowIndex) => {
    let input = value.trim();

    // Handle input without colon
    if (!input.includes(":")) {
      const digitsOnly = input.replace(/\D/g, "");
      if (digitsOnly.length === 1) {
        input = `0${digitsOnly}:00`; // "3" → "03:00"
      } else if (digitsOnly.length === 2) {
        input = `${digitsOnly}:00`; // "12" → "12:00"
      }
    }

    // const formatted = formatTimeWithColon(input); // Force to HH:MM
    const formatted = input; // Force to HH:MM
    const updated = [...timesheetFinalApprovalData];

    // const approvedMinutes = timeToMinutes(formatted);
    // const workedMinutes = timeToMinutes(updated[rowIndex]?.hr_approved_hours || "00:00");

    // if (approvedMinutes > workedMinutes) {
    //   Swal.fire({
    //     icon: 'warning',
    //     title: 'Invalid Entry',
    //     text: 'Approved hours cannot be greater than total approved hours.',
    //   });
    //   updated[rowIndex].approved_hours = updated[rowIndex].hr_approved_hours;
    // } else {
      updated[rowIndex].approved_hours = formatted;
    // }

    setTimesheetFinalApprovalData(updated); // Ensure update reflects globally
  };

  const handleRemarksChange = (sno, value) => {
    const updatedData = (timesheetFinalApprovalData || []).map((item) =>
      item.emp_id === sno ? { ...item, remarks: value } : item
    );
    setTimesheetFinalApprovalData(updatedData);
  };

  const columns = useMemo(() => {
    const staticColumns = [     
     
      {
        Header: "EMPLOYEE HOURS",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [          
          {
            id: "selection",
            className: "serial-no",
            applyHeaderClass: true,
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div className="text-center">
                <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            Cell: ({ row }) => (
              <div className="text-center">
                <input type="checkbox" {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          {
            Header: "Approve Hrs",
            // accessor: "total_approved_hours",
            className: "bg-approve",
            applyHeaderClass: true,
            Cell: ({ row }) => {
              const rowIndex = row.index;
              const [value, setValue] = useState(timesheetFinalApprovalData[rowIndex]?.approved_hours || "");

              const handleChange = (e) => {
                const input = e.target.value;

                // Allow only digits and one colon
                if (!/^[0-9:]*$/.test(input)) return;

                // Only one colon allowed
                if ((input.match(/:/g) || []).length > 1) return;

                // Split input into hours and minutes
                const [hours, minutes] = input.split(":");

                if (hours?.length > 3) return;
                if (minutes?.length > 2) return;

                setValue(input);
              };

              const handleBlur = () => {
                const regex = /^(\d{1,3}):([0-5][0-9])$/;

                if (regex.test(value)) {
                  handleHourChange(value, rowIndex);
                } else {
                  Swal.fire({
                    icon: "error",
                    title: "Invalid Time Format",
                    text: "Please enter time in HHH:MM format.\nHours (max 3 digits) and Minutes (00–59).",
                    confirmButtonColor: "#d33"
                  });
                  setValue("");
                }
              };

              return (
                <Input
                  type="text"
                  value={value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="HH:MM"
                  style={{ width: "80px", padding: "4px" }}
                />
              );
            },
          },
          {
            Header: "Remarks",
            // accessor: "remarks",
            className: "bg-approve",
            applyHeaderClass: true,
            Cell: ({ row }) => {
              const rowIndex = row.index;
              const [value, setValue] = useState(timesheetFinalApprovalData[rowIndex]?.remarks || "");

              const handleBlur = () => {
                if (value) {
                  handleRemarksChange(row.original.emp_id, value);
                }
              };

              return (
                <Input
                  type="text"
                  bsSize="sm"
                  placeholder="Enter remarks"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onBlur={handleBlur} // only update global state on focus out
                />
              );
            },
          },
          { Header: "Code", accessor: "emp_no" },
          { Header: "Name", accessor: "emp_name" },
          { Header: "Month", accessor: "salary_month" },          
        ]
      },
       {
        Header: "ATTENDNACE",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          { Header: "Working Days", applyHeaderClass: true, className: "bg-leave", accessor: "total_days", className: "bg-leave text-center", },
          {
            Header: "Present",
            accessor: "present",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Leave",
            accessor: "leave_days",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
           {
            Header: "Week Off",
            accessor: "week_off",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Holiday",
            accessor: "holiday",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
        ],
      },
       {
        Header: "PROJECT",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            Header: "Billable",
            accessor: "billable_hours",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Non Billable",
            accessor: "non_billable_hours",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
        ],
      },
      {
        Header: "NON PROJECT",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            Header: "AMC",
            accessor: "amc_hours",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Pre Sales",
            accessor: "pre_sales",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Billable",
            accessor: "non_projects_billable",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Non Billable",
            accessor: "non_project_nonbillable",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
        ],
      },
      //  {
      //   Header: "TOTAL",
      //   accessor: "total_worked_hours",
      //   className: "bg-total text-center",
      //   applyHeaderClass: true,
      // },
      {
        Header: "TOTAL",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            Header: "ACTUAL HOURS",
            accessor: "shift_minutes",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
           {
            Header: "PUNCH HOURS",
            accessor: "punch_hours",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
          {
            Header: "TIMESHEET HOURS",
            accessor: "total_approved_hours",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
        ],
      },
      {
        Header: "APPROVED",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [         
          {
            Header: "MANAGER",
            accessor: "manager_approved_hours",
            className: "bg-worked text-center",
            applyHeaderClass: true,
          },
          {
            Header: "HR",
            accessor: "hr_approved_hours",
            className: "bg-worked text-center",
            applyHeaderClass: true,
          },
        ],
      }, 

      // {
      //   Header: "T.Worked Hrs",
      //   accessor: "total_worked_hours",
      //   className: "bg-worked",
      //   applyHeaderClass: true,
      // },
      // {
      //   Header: "Mgr Approved Hrs",
      //   accessor: "manager_approved_hours",
      //   className: "bg-worked",
      //   applyHeaderClass: true,
      // },
      // {
      //   Header: "HR Approved Hrs",
      //   accessor: "hr_approved_hours",
      //   className: "bg-worked",
      //   applyHeaderClass: true,
      // },
    ];

    const dynamicDateCols = getDateColumns(formik.values?.month);
    return [...staticColumns, ...dynamicDateCols];
  }, [timesheetFinalApprovalData,formik.values?.month]);

  useEffect(()=>{
    if (!formik.values?.month) {
      dispatch(resetTimesheetFinalApproval());
    }
  },[formik.values?.month])

  const handleSelectedRowsChange = React.useCallback((rows) => {
    setSelectedRows(rows);
  }, []);

  const handleApprove = ()=>{
    if (!selectedRows || selectedRows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No records selected',
        text: 'Please select at least one employee to approve timesheet hours.'
      });
      return;
    }
    const formatted = {
      timesheet: selectedRows.map(row => ({
        emp_id: String(row.emp_id),
        approval_status: "1",
        approval_hours: row.approved_hours,
        approval_remarks: row.remarks
      }))
    };
    const formData = new FormData();
    formData.append('lst_timesheet_hrapproval',JSON.stringify(formatted))
    formData.append('create_by',authDetails?.user_id)
    formData.append('unit_id',authDetails?.unit_id)
    dispatch(saveTimesheetFinalApproval(formData))
  }

  return (
    <div className="page-content">
    <Loader loading={loading}/>
      <Container fluid>
        <Breadcrumbs title="Time Sheet" breadcrumbItem="Final Approval" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row >
                <Col md={3}>
                  <FormGroup>
                    <Label>Month <span className="text-danger">*</span></Label>
                    <CustomMonthPicker
                      name="month"
                      value={formik.values.month}
                      onChange={(date) => formik.setFieldValue("month", date)}
                      onBlur={formik.handleBlur}
                      disableFuture={true}
                      className={`form-control ${formik.touched.month && formik.errors.month ? "is-invalid" : ""}`}
                    />
                    {formik.touched.month && formik.errors.month && (
                      <div className="text-danger">{formik.errors.month}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col className="d-flex align-items-center justify-content-end">
                  <Button type="submit" className="btn button-search me-2">
                    <i className="fas fa-search me-1"/> Search
                  </Button>
                  <Button type="reset" className="btn button-cancel me-2" onClick={formik.resetForm}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  {canApprove && (
                    <Button type="button" className="btn button-save me-2" onClick={handleApprove}>
                      <i className="fas fa-check-circle me-1" /> Approve
                    </Button>
                  )}
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody style={{ overflowX: "auto" }}>
            <TableContainer
              columns={columns}
              data={timesheetFinalApprovalData && timesheetFinalApprovalData.length > 0 ? timesheetFinalApprovalData:[]}
              onSelectedRowsChange={handleSelectedRowsChange}
              customPageSize={10}
              isGlobalFilter={true}
              className="custom-header-css"
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TimeSheetFinalApproval;